import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

import { ref, computed, watch } from 'vue';
import {
  startOfToday,
  addDays,
  addMonths,
  addYears,
  subDays,
  subMonths,
  subYears,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  format
} from 'date-fns';

import CalendarMonth from './CalendarMonth.vue';
import CalendarWeek from './CalendarWeek.vue';
import CalendarDay from './CalendarDay.vue';
import CalendarYear from './CalendarYear.vue';

import { useActivitiesStore } from '@/store/activities';
import { configRegistry } from '@/lib/configRegistry';
import { useFloatFormStore } from '@/store/floatForm';


export default /*@__PURE__*/_defineComponent({
  __name: 'Container',
  props: {
    moduleName: {},
    title: {}
  },
  setup(__props: any) {

const { openForm } = useFloatFormStore();

const props = __props;

const calendarModes = ['Día', 'Semana', 'Mes', 'Año'];
const calendarMode = ref<'Día' | 'Semana' | 'Mes' | 'Año'>('Mes');

const baseDate = ref(startOfToday());
const selectedMonth = ref(baseDate.value.getMonth());
const selectedYear = ref(baseDate.value.getFullYear());

const months = Array.from({ length: 12 }, (_, i) => ({
  title: new Date(2000, i).toLocaleString('default', { month: 'long' }),
  value: i,
}));
const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

watch([selectedMonth, selectedYear], () => {
  baseDate.value = new Date(selectedYear.value, selectedMonth.value);
});

const activeViewComponent = computed(() => {
  switch (calendarMode.value) {
    case 'Día': return CalendarDay;
    case 'Semana': return CalendarWeek;
    case 'Mes': return CalendarMonth;
    case 'Año': return CalendarYear;
  }
});

function goToToday() {
  const today = startOfToday();
  baseDate.value = today;
  selectedMonth.value = today.getMonth();
  selectedYear.value = today.getFullYear();
}

function goToNext() {
  switch (calendarMode.value) {
    case 'Día': baseDate.value = addDays(baseDate.value, 1); break;
    case 'Semana': baseDate.value = addDays(baseDate.value, 7); break;
    case 'Mes': baseDate.value = addMonths(baseDate.value, 1); selectedMonth.value = baseDate.value.getMonth(); selectedYear.value = baseDate.value.getFullYear(); break;
    case 'Año': baseDate.value = addYears(baseDate.value, 1); break;
  }
}

function goToPrevious() {
  switch (calendarMode.value) {
    case 'Día': baseDate.value = subDays(baseDate.value, 1); break;
    case 'Semana': baseDate.value = subDays(baseDate.value, 7); break;
    case 'Mes': baseDate.value = subMonths(baseDate.value, 1); selectedMonth.value = baseDate.value.getMonth(); selectedYear.value = baseDate.value.getFullYear(); break;
    case 'Año': baseDate.value = subYears(baseDate.value, 1); break;
  }
}

const showMonthSelect = computed(() => calendarMode.value === 'Mes');
const showYearSelect = computed(() => calendarMode.value !== 'Día');

const store = useActivitiesStore();
const rawEvents = computed(() => store.activities);

const calendarConfig = ref<{ dateField: string; filterField: string; labelField: string; colorField?: string }>({ dateField: 'date', filterField: 'date_between', labelField: 'name' });

const drawerVisible = ref(false);
const drawerComponent = ref<any>(null);
const selectedEventId = ref<number | null>(null);

async function openDetailDrawer(event: any) {
  try {
    const loader = configRegistry[props.moduleName];
    if (!loader) throw new Error('No config');
    const mod = await loader();
    if (mod.DETAIL?.component) {
      drawerComponent.value = (await mod.DETAIL.component()).default;
      selectedEventId.value = event.id;
      drawerVisible.value = true;
    } else {
      console.warn(`[Calendar] No se encontró DETAIL.component en config para módulo ${props.moduleName}`);
    }
  } catch (err) {
    console.error('[Calendar] Error cargando componente de detalle:', err);
  }
}

(async () => {
  try {
    const loader = configRegistry[props.moduleName];
    if (!loader) throw new Error('No config');
    const mod = await loader();
    if (mod.CALENDAR_CONFIG) calendarConfig.value = mod.CALENDAR_CONFIG;
  } catch (err) {
    console.warn('[CalendarContainer] No se pudo cargar CALENDAR_CONFIG:', err);
  }
})();

const mappedEvents = computed(() => {
  const { dateField, labelField, colorField } = calendarConfig.value;
  return rawEvents.value.map(event => {
    const date = getValueByPath(event, dateField);
    const label = getValueByPath(event, labelField);
    const color = colorField ? getValueByPath(event, colorField) : 'primary';
    return { id: event.id, date, name: label, color, original: event };
  });
});

function getValueByPath(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

function openCreateForm(data: { date: string | [string, string] }) {
  openForm({
    component: 'CreateForm',
    formTitle: 'Crear ' + props.title?.singular,
    formWidth: '1080',
    module: props.moduleName,
    props: {
      initialValues: {
        date: data.date
      }
    }
  });
}

const dateRange = computed(() => {
  const field = calendarConfig.value.filterField || 'date';
  let start: Date, end: Date;

  switch (calendarMode.value) {
    case 'Día':
      start = baseDate.value;
      end = baseDate.value;
      break;
    case 'Semana':
      start = startOfWeek(baseDate.value, { weekStartsOn: 1 });
      end = endOfWeek(baseDate.value, { weekStartsOn: 1 });
      break;
    case 'Mes':
    default:
      start = startOfWeek(startOfMonth(baseDate.value), { weekStartsOn: 1 });
      end = endOfWeek(endOfMonth(baseDate.value), { weekStartsOn: 1 });
      break;
  }

  return {
    [`filter[${field}]`]: `${format(start, 'yyyy-MM-dd')},${format(end, 'yyyy-MM-dd')}`
  };
});

watch(
  [dateRange, calendarConfig],
  async () => {
    try {
      await store.fetchActivities(dateRange.value);
    } catch (err) {
      console.error('[CalendarContainer] Error fetching activities:', err);
    }
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_navigation_drawer = _resolveComponent("v-navigation-drawer")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, { fluid: "" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { class: "pa-4 pt-3" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_row, { class: "ma-0 mb-1" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                class: "mr-4 my-2",
                density: "compact",
                variant: "text",
                onClick: goToToday
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { class: "mr-2" }, {
                    default: _withCtx(() => _cache[4] || (_cache[4] = [
                      _createTextVNode("mdi-calendar-import-outline")
                    ])),
                    _: 1
                  }),
                  _cache[5] || (_cache[5] = _createTextVNode(" Hoy "))
                ]),
                _: 1
              }),
              _createVNode(_component_v_btn, {
                class: "pt-2 mx-1",
                icon: "",
                variant: "text",
                density: "compact",
                onClick: goToPrevious
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, null, {
                    default: _withCtx(() => _cache[6] || (_cache[6] = [
                      _createTextVNode("mdi-chevron-left")
                    ])),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_btn, {
                class: "pt-2 mx-1",
                icon: "",
                variant: "text",
                density: "compact",
                onClick: goToNext
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, null, {
                    default: _withCtx(() => _cache[7] || (_cache[7] = [
                      _createTextVNode("mdi-chevron-right")
                    ])),
                    _: 1
                  })
                ]),
                _: 1
              }),
              (showMonthSelect.value)
                ? (_openBlock(), _createBlock(_component_v_select, {
                    key: 0,
                    class: "month_select ml-2",
                    modelValue: selectedMonth.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedMonth).value = $event)),
                    items: _unref(months),
                    "item-title": "title",
                    "item-value": "value",
                    density: "compact",
                    variant: "solo",
                    "hide-details": "",
                    "max-width": "170"
                  }, null, 8, ["modelValue", "items"]))
                : _createCommentVNode("", true),
              (showYearSelect.value)
                ? (_openBlock(), _createBlock(_component_v_select, {
                    key: 1,
                    class: "month_select",
                    modelValue: selectedYear.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedYear).value = $event)),
                    items: _unref(years),
                    density: "compact",
                    "hide-details": "",
                    "max-width": "120",
                    variant: "solo"
                  }, null, 8, ["modelValue", "items"]))
                : _createCommentVNode("", true),
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_menu, null, {
                activator: _withCtx(({ props }) => [
                  _createVNode(_component_v_btn, _mergeProps(props, {
                    style: {"margin":"2px"},
                    class: "elevation-0"
                  }), {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, { class: "mr-2" }, {
                        default: _withCtx(() => _cache[8] || (_cache[8] = [
                          _createTextVNode("mdi-calendar-week")
                        ])),
                        _: 1
                      }),
                      _createTextVNode(" " + _toDisplayString(calendarMode.value), 1)
                    ]),
                    _: 2
                  }, 1040)
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_v_list, null, {
                    default: _withCtx(() => [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(calendarModes, (mode, index) => {
                        return _createVNode(_component_v_list_item, {
                          key: index,
                          onClick: ($event: any) => (calendarMode.value = mode)
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_list_item_title, null, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(mode), 1)
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          _: 2
                        }, 1032, ["onClick"])
                      }), 64))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          (_openBlock(), _createBlock(_resolveDynamicComponent(activeViewComponent.value), {
            "base-date": baseDate.value,
            events: mappedEvents.value,
            "calendar-config": calendarConfig.value,
            "on-create": openCreateForm,
            "on-view": openDetailDrawer,
            title: props.title,
            "module-name": props.moduleName
          }, null, 40, ["base-date", "events", "calendar-config", "title", "module-name"]))
        ]),
        _: 1
      }),
      _createVNode(_component_v_navigation_drawer, {
        class: "float-drawer",
        modelValue: drawerVisible.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((drawerVisible).value = $event)),
        location: "right",
        width: "500",
        temporary: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_btn, {
            onClick: _cache[2] || (_cache[2] = ($event: any) => (drawerVisible.value=false))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, null, {
                default: _withCtx(() => _cache[9] || (_cache[9] = [
                  _createTextVNode("mdi-chevron-left")
                ])),
                _: 1
              })
            ]),
            _: 1
          }),
          (drawerComponent.value)
            ? (_openBlock(), _createBlock(_resolveDynamicComponent(drawerComponent.value), {
                key: 0,
                id: selectedEventId.value
              }, null, 8, ["id"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})